import React, { useEffect, useRef } from 'react'
import {
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
  InputAdornment,
  Paper,
  ClickAwayListener
} from '@mui/material'
import { Search, Clear } from '@mui/icons-material'
import { useAutoCompleter } from './useAutoCompleter'
import { AutoCompleterProps } from './types'

export function AutoCompleterV3<T>({
  config,
  value,
  onChange,
  placeholder = 'Buscar...',
  label,
  disabled = false,
  error = false,
  helperText,
  renderOption,
  renderValue
}: AutoCompleterProps<T>) {
  const {
    items,
    loading,
    error: searchError,
    searchText,
    selectedItem,
    isOpen,
    handleSearchChange,
    handleItemSelect,
    handleOpen,
    handleClose,
    clearSelection
  } = useAutoCompleter<T>(config)

  const anchorRef = useRef<HTMLDivElement>(null)

  // Sincronizar con el valor externo solo cuando cambia
  useEffect(() => {
    if (value && selectedItem && value !== selectedItem) {
      // Si hay un valor externo pero no coincide con el seleccionado, limpiar
      clearSelection()
    }
  }, [value, selectedItem, clearSelection])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchText = event.target.value
    handleSearchChange(newSearchText)
    if (!isOpen) {
      handleOpen()
    }
  }

  const handleMenuItemClick = (item: T) => {
    handleItemSelect(item)
    onChange(item)
    handleClose()
  }

  const handleClear = () => {
    clearSelection()
    onChange(null)
  }

  const handleInputFocus = () => {
    handleOpen()
  }

  const handleClickAway = () => {
    handleClose()
  }

  const displayValue = selectedItem 
    ? (renderValue ? renderValue(selectedItem) : String(selectedItem[config.displayField] || ''))
    : (searchText || '')

  const hasError = error || !!searchError
  const errorMessage = searchError || helperText

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div ref={anchorRef} style={{ position: 'relative', width: '100%' }}>
        <TextField
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          variant="outlined"
          label={label}
          placeholder={placeholder}
          disabled={disabled}
          error={hasError}
          helperText={errorMessage}
          autoComplete="off"
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {loading ? (
                  <CircularProgress size={20} />
                ) : selectedItem ? (
                  <Clear 
                    style={{ cursor: 'pointer' }}
                    onClick={handleClear}
                  />
                ) : (
                  <Search />
                )}
              </InputAdornment>
            )
          }}
        />
        
        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              width: anchorRef.current?.offsetWidth || 'auto',
              zIndex: 1300
            }}
          >
            <Paper elevation={3} style={{ maxHeight: 300, overflow: 'auto' }}>
            {/* Mensaje cuando no hay suficientes caracteres */}
            {searchText && typeof searchText === 'string' && searchText.length > 0 && searchText.length < (config.minSearchLength || 2) && (
              <MenuItem disabled>
                <Typography variant="body2" color="textSecondary">
                  Escriba al menos {config.minSearchLength || 2} caracteres
                </Typography>
              </MenuItem>
            )}

            {/* Mensaje cuando no hay resultados */}
            {searchText && typeof searchText === 'string' && searchText.length >= (config.minSearchLength || 2) && 
             !loading && 
             Array.isArray(items) && items.length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2" color="textSecondary">
                  No se encontraron resultados
                </Typography>
              </MenuItem>
            )}

            {/* Opciones encontradas */}
            {Array.isArray(items) && items.map((item, index) => (
              <MenuItem 
                key={index} 
                onClick={() => handleMenuItemClick(item)}
                style={{ cursor: 'pointer' }}
              >
                {renderOption ? renderOption(item) : String(item[config.displayField])}
              </MenuItem>
            ))}
            </Paper>
          </div>
        )}
      </div>
    </ClickAwayListener>
  )
}
